import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ClusterSummary, ScatterPoint } from '../services/embeddingService'

/**
 * 2048차원 임베딩을 PCA 로 3차원에 투영한 점들을 canvas 에 직접 그린다.
 * 저장소에 차트 라이브러리가 없어(기존 차트도 손으로 짠 SVG) 회전·투영을 직접 계산한다.
 * 점이 수천 개라 SVG 노드로 만들면 느려지므로 canvas 를 쓴다.
 */

export type CameraMode = 'rotate' | 'pan'
export type Backdrop = 'dark' | 'light'

/** 배경마다 축·점 표현을 따로 잡는다. 어두운 배경용 파스텔 축은 흰 배경에서
 *  흐려 보이지 않고, 발광 강조도 흰 배경에서는 아무 효과가 없어 테두리로 바꾼다. */
interface BackdropTheme {
  bg: string
  /** X·Y·Z 축 색 */
  axes: [string, string, string]
  /** 선택이 없을 때 점 alpha */
  alpha: number
  /** 선택이 있을 때 비선택 점 alpha */
  dimAlpha: number
  /** 선택된 점의 발광 반경 (0 이면 발광 없음) */
  glow: number
  /** 선택된 점의 테두리 색 (null 이면 그리지 않음) */
  ring: string | null
  /** 점 색을 검정 쪽으로 섞는 비율. 팔레트에는 amber·lime·stone 처럼 밝은 색이
   *  섞여 있어 흰 배경에서는 그대로 쓰면 배경에 묻힌다. */
  darken: number
}

/** hex 색을 검정 쪽으로 amount(0~1) 만큼 당긴다 */
const shade = (hex: string, amount: number) => {
  if (!amount) return hex
  const n = parseInt(hex.slice(1), 16)
  const mix = (c: number) => Math.round(c * (1 - amount))
  const r = mix((n >> 16) & 255), g = mix((n >> 8) & 255), b = mix(n & 255)
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`
}

const THEMES: Record<Backdrop, BackdropTheme> = {
  dark: {
    bg: '#0a0a0f',
    axes: ['#f87171', '#4ade80', '#60a5fa'],
    // 어두운 배경에서는 흐린 점이 배경에 묻히므로 흰 배경 때보다 alpha 를 올린다
    alpha: 0.85,
    dimAlpha: 0.28,
    glow: 8,
    ring: null,
    darken: 0,
  },
  light: {
    bg: '#ffffff',
    axes: ['#dc2626', '#16a34a', '#2563eb'],
    // 흰 배경에서는 반투명하면 곧바로 흐려 보인다. 불투명하게 두고 색을 어둡게 당긴다.
    alpha: 1,
    dimAlpha: 0.3,
    glow: 0,
    ring: 'rgba(15,23,42,0.8)',
    darken: 0.22,
  },
}

// 최종 묶음이 40개를 넘으므로 15색으로는 서로 다른 묶음이 같은 색으로 보인다.
// 앞 15개는 기존 값을 그대로 두고(이미 익숙한 색) 뒤에 구분되는 색을 더했다.
export const CLUSTER_COLORS = [
  '#6366f1', '#ef4444', '#3b82f6', '#14b8a6', '#f59e0b',
  '#8b5cf6', '#10b981', '#ec4899', '#a8a29e', '#0ea5e9',
  '#f97316', '#84cc16', '#e11d48', '#7c3aed', '#06b6d4',
  '#be123c', '#0f766e', '#b45309', '#4338ca', '#65a30d',
  '#c026d3', '#0891b2', '#9a3412', '#1d4ed8', '#15803d',
  '#a21caf', '#0369a1', '#78350f', '#4c1d95', '#166534',
]

export const colorOf = (clusterIdx: number) => CLUSTER_COLORS[clusterIdx % CLUSTER_COLORS.length]

interface View {
  yaw: number
  pitch: number
  zoom: number
  panX: number
  panY: number
}

// 초기 줌은 1 이면 점 구름이 화면 가운데에 작게 뭉쳐 보인다. 처음부터 채워 보이게 키운다.
const INITIAL_VIEW: View = { yaw: -0.6, pitch: -0.35, zoom: 1.8, panX: 0, panY: 0 }

/** 회전 감도(라디안/픽셀). 0.011 이면 100px 드래그에 약 63도 돌아간다. */
const ROTATE_SPEED = 0.011

const TAU = Math.PI * 2
/** 각도를 -π~π 로 감는다 (무한 회전에서 값이 계속 커지는 것을 막는다) */
const wrapAngle = (a: number) => {
  const m = ((a + Math.PI) % TAU + TAU) % TAU
  return m - Math.PI
}

interface Props {
  points: ScatterPoint[]
  clusters: ClusterSummary[]
  selectedClusterId: number | null
  onSelectCluster: (clusterId: number) => void
  pointSize: number
  showLabels: boolean
  mode: CameraMode
  backdrop: Backdrop
  resetSignal: number
}

/** 회전 행렬 적용 후 원근 투영. 반환 z 는 깊이 정렬용. */
function project(p: { x: number; y: number; z: number }, view: View, w: number, h: number) {
  const cy = Math.cos(view.yaw), sy = Math.sin(view.yaw)
  const cp = Math.cos(view.pitch), sp = Math.sin(view.pitch)
  // Y축(yaw) → X축(pitch) 순서로 회전
  const x1 = p.x * cy - p.z * sy
  const z1 = p.x * sy + p.z * cy
  const y2 = p.y * cp - z1 * sp
  const z2 = p.y * sp + z1 * cp
  const depth = z2 + 3.2                     // 카메라를 뒤로 물려 원근 분모를 양수로 유지
  const persp = 2.6 / Math.max(0.35, depth)
  const scale = Math.min(w, h) * 0.34 * view.zoom * persp
  return {
    sx: w / 2 + x1 * scale + view.panX,
    sy: h / 2 - y2 * scale + view.panY,
    depth,
  }
}

export default function ClusterScatter3D({
  points, clusters, selectedClusterId, onSelectCluster,
  pointSize, showLabels, mode, backdrop, resetSignal,
}: Props) {
  const theme = THEMES[backdrop]
  // 배경에 맞춰 보정한 점 색. 매 프레임 계산하지 않도록 배경이 바뀔 때만 만든다.
  const palette = useMemo(() => CLUSTER_COLORS.map(c => shade(c, theme.darken)), [theme])
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const wrapRef = useRef<HTMLDivElement>(null)
  const [view, setView] = useState<View>(INITIAL_VIEW)
  const [size, setSize] = useState({ w: 640, h: 520 })
  const drag = useRef<{ x: number; y: number; mode: CameraMode; moved: number } | null>(null)
  // 드래그로 회전한 뒤 마우스를 떼면 브라우저가 click 을 발생시킨다. 그대로 두면
  // 손을 뗀 자리의 클러스터가 선택되므로, 일정 거리 이상 움직인 제스처는 클릭에서 제외한다.
  const suppressClick = useRef(false)
  const DRAG_SLOP = 5
  const [hover, setHover] = useState<{ x: number; y: number; label: string } | null>(null)

  useEffect(() => { setView(INITIAL_VIEW) }, [resetSignal])

  // 컨테이너 크기에 맞춰 캔버스 해상도를 잡는다 (고DPI 대응)
  useEffect(() => {
    const el = wrapRef.current
    if (!el) return
    const ro = new ResizeObserver(entries => {
      const r = entries[0].contentRect
      setSize({ w: Math.max(320, r.width), h: Math.max(320, r.height) })
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const idxByCluster = useMemo(() => {
    const m = new Map<number, number>()
    clusters.forEach(c => m.set(c.id, c.cluster_idx))
    return m
  }, [clusters])

  /** 클러스터별 중심의 화면 좌표 — 라벨 말풍선 위치 */
  const labelAnchors = useMemo(() => {
    if (!showLabels) return []
    const acc = new Map<number, { x: number; y: number; z: number; n: number }>()
    for (const p of points) {
      const a = acc.get(p.cluster_id) || { x: 0, y: 0, z: 0, n: 0 }
      a.x += p.x; a.y += p.y; a.z += p.z; a.n += 1
      acc.set(p.cluster_id, a)
    }
    return clusters
      .filter(c => acc.has(c.id))
      .map(c => {
        const a = acc.get(c.id)!
        const pr = project({ x: a.x / a.n, y: a.y / a.n, z: a.z / a.n }, view, size.w, size.h)
        return { id: c.id, label: c.label, sx: pr.sx, sy: pr.sy, depth: pr.depth }
      })
      // 캔버스 밖으로 나간 이름표는 그리지 않는다. 확대하면 중심이 화면 밖으로
      // 밀려나는데 이름표는 DOM 이라 상자를 넘어 페이지 위로 떠버린다.
      .filter(a => a.sx >= -40 && a.sx <= size.w + 40 && a.sy >= -20 && a.sy <= size.h + 20)
      .sort((a, b) => b.depth - a.depth)
  }, [points, clusters, view, size, showLabels])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const dpr = window.devicePixelRatio || 1
    canvas.width = size.w * dpr
    canvas.height = size.h * dpr
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    ctx.fillStyle = theme.bg
    ctx.fillRect(0, 0, size.w, size.h)

    // 정육면체 대신 원점을 지나는 세 축만 그린다. 음의 방향은 흐리게 두어
    // 어느 쪽이 + 인지 바로 읽히게 한다.
    const AXES: { v: [number, number, number]; color: string; label: string }[] = [
      { v: [1, 0, 0], color: theme.axes[0], label: 'X' },
      { v: [0, 1, 0], color: theme.axes[1], label: 'Y' },
      { v: [0, 0, 1], color: theme.axes[2], label: 'Z' },
    ]
    const origin = project({ x: 0, y: 0, z: 0 }, view, size.w, size.h)
    const L = 1.15
    ctx.lineWidth = 1.4
    for (const axis of AXES) {
      const [ax, ay, az] = axis.v
      const pos = project({ x: ax * L, y: ay * L, z: az * L }, view, size.w, size.h)
      const neg = project({ x: -ax * L, y: -ay * L, z: -az * L }, view, size.w, size.h)
      // 음의 방향
      ctx.strokeStyle = axis.color + '33'
      ctx.beginPath(); ctx.moveTo(origin.sx, origin.sy); ctx.lineTo(neg.sx, neg.sy); ctx.stroke()
      // 양의 방향
      ctx.strokeStyle = axis.color + 'cc'
      ctx.beginPath(); ctx.moveTo(origin.sx, origin.sy); ctx.lineTo(pos.sx, pos.sy); ctx.stroke()
      // 축 끝 마커와 라벨
      ctx.fillStyle = axis.color
      ctx.beginPath(); ctx.arc(pos.sx, pos.sy, 2.4, 0, Math.PI * 2); ctx.fill()
      ctx.font = '600 12px system-ui, -apple-system, sans-serif'
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      const dx = pos.sx - origin.sx, dy = pos.sy - origin.sy
      const len = Math.max(1, Math.hypot(dx, dy))
      ctx.fillText(axis.label, pos.sx + (dx / len) * 12, pos.sy + (dy / len) * 12)
    }

    // 먼 점부터 그려야 가까운 점이 위에 온다
    const projected = points.map(p => {
      const pr = project(p, view, size.w, size.h)
      return { p, ...pr }
    }).sort((a, b) => b.depth - a.depth)

    const hasSelection = selectedClusterId !== null
    for (const item of projected) {
      const isSel = item.p.cluster_id === selectedClusterId
      const idx = idxByCluster.get(item.p.cluster_id) ?? 0
      // 원근에 따라 점 크기를 조금 변화시켜 깊이감을 준다
      const r = pointSize * (2.6 / Math.max(0.35, item.depth)) * 0.9
      ctx.globalAlpha = hasSelection ? (isSel ? 1 : theme.dimAlpha) : theme.alpha
      ctx.fillStyle = palette[idx % palette.length]
      // 선택된 클러스터만 은은하게 발광시켜 검은 배경에서 눈에 걸리게 한다
      ctx.shadowBlur = isSel && hasSelection ? theme.glow : 0
      ctx.shadowColor = isSel && hasSelection && theme.glow ? palette[idx % palette.length] : 'transparent'
      const rr = Math.max(0.6, r)
      ctx.beginPath()
      ctx.arc(item.sx, item.sy, rr, 0, Math.PI * 2)
      ctx.fill()
      // 흰 배경에서는 발광이 보이지 않으므로 얇은 테두리로 선택을 표시한다
      if (isSel && hasSelection && theme.ring) {
        ctx.strokeStyle = theme.ring
        ctx.lineWidth = 1
        ctx.beginPath()
        ctx.arc(item.sx, item.sy, rr + 0.8, 0, Math.PI * 2)
        ctx.stroke()
      }
    }
    ctx.shadowBlur = 0
    ctx.globalAlpha = 1
  }, [points, view, size, pointSize, selectedClusterId, idxByCluster, theme, palette])

  const onDown = (e: React.PointerEvent) => {
    // 포인터를 캔버스에 고정한다. 이게 없으면 회전 중 커서가 캔버스 위에 떠 있는
    // 클러스터 라벨 버튼을 지나갈 때 canvas 의 mouseleave 가 떠서 드래그가 끊긴다
    // (좌우로 왕복할 때 어떤 때는 돌고 어떤 때는 멈추던 원인).
    try { e.currentTarget.setPointerCapture(e.pointerId) } catch { /* 미지원 브라우저는 무시 */ }
    drag.current = { x: e.clientX, y: e.clientY, mode: e.shiftKey ? 'pan' : mode, moved: 0 }
    suppressClick.current = false
  }
  const onMove = (e: React.PointerEvent | React.MouseEvent) => {
    if (!drag.current) return
    const dx = e.clientX - drag.current.x
    const dy = e.clientY - drag.current.y
    // updater 안에서 drag.current 를 읽으면 안 된다 — updater 는 지연 실행될 수 있고
    // 그 사이 mouseup 으로 null 이 되면 터진다. 여기서 값으로 캡처한다.
    const dragMode = drag.current.mode
    drag.current.x = e.clientX
    drag.current.y = e.clientY
    drag.current.moved += Math.abs(dx) + Math.abs(dy)
    if (drag.current.moved > DRAG_SLOP) suppressClick.current = true
    setView(v => dragMode === 'pan'
      ? { ...v, panX: v.panX + dx, panY: v.panY + dy }
      : {
          ...v,
          // 상하 회전은 제한하지 않는다 — 뒤집힌 상태를 허용해 무한히 돌아간다.
          // 각도는 2π 로 감아 오래 돌려도 부동소수 오차가 누적되지 않게 한다.
          yaw: wrapAngle(v.yaw + dx * ROTATE_SPEED),
          pitch: wrapAngle(v.pitch + dy * ROTATE_SPEED),
        })
  }
  const onUp = () => { drag.current = null }

  // 줌은 native wheel 리스너로 처리한다. React 의 onWheel 은 passive 로 등록되어
  // preventDefault 가 무시되고, 그러면 확대와 함께 페이지도 같이 스크롤된다.
  // 리스너는 캔버스가 아니라 래퍼에 건다 — 캔버스 위에 떠 있는 클러스터 라벨 버튼
  // 위에서 휠을 굴리면 캔버스 리스너를 타지 않고 부모로 버블링되어 페이지가 스크롤됐다.
  // 래퍼에서 받으면 자식 어디에서 시작된 휠이든 여기서 막을 수 있다.
  useEffect(() => {
    const wrap = wrapRef.current
    if (!wrap) return
    const handler = (e: WheelEvent) => {
      e.preventDefault()
      setView(v => ({
        ...v,
        zoom: Math.max(0.35, Math.min(6, v.zoom * (e.deltaY < 0 ? 1.12 : 0.89))),
      }))
    }
    wrap.addEventListener('wheel', handler, { passive: false })
    return () => wrap.removeEventListener('wheel', handler)
  }, [])

  /** 클릭 지점에서 가장 가까운 점의 클러스터를 선택 */
  const pick = useCallback((e: React.MouseEvent) => {
    const rect = canvasRef.current?.getBoundingClientRect()
    if (!rect) return null
    const mx = e.clientX - rect.left
    const my = e.clientY - rect.top
    let best: { id: number; d: number } | null = null
    for (const p of points) {
      const pr = project(p, view, size.w, size.h)
      const d = (pr.sx - mx) ** 2 + (pr.sy - my) ** 2
      if (!best || d < best.d) best = { id: p.cluster_id, d }
    }
    return best && best.d <= 24 * 24 ? best.id : null
  }, [points, view, size])

  return (
    <div ref={wrapRef} className="relative h-full w-full select-none overflow-hidden">
      <canvas
        ref={canvasRef}
        style={{ width: size.w, height: size.h, cursor: drag.current ? 'grabbing' : 'grab' }}
        onPointerDown={onDown}
        onPointerMove={e => {
          onMove(e)
          if (!drag.current) {
            const id = pick(e)
            const c = clusters.find(x => x.id === id)
            const rect = canvasRef.current?.getBoundingClientRect()
            setHover(c && rect
              ? { x: e.clientX - rect.left, y: e.clientY - rect.top, label: `${c.label} · ${c.size.toLocaleString()}장` }
              : null)
          }
        }}
        onPointerUp={e => {
          try { e.currentTarget.releasePointerCapture(e.pointerId) } catch { /* noop */ }
          onUp()
        }}
        onPointerCancel={onUp}
        onPointerLeave={() => { if (!drag.current) setHover(null) }}
        onClick={e => {
          // 회전·이동 제스처의 끝에서 발생한 click 은 무시한다
          if (suppressClick.current) { suppressClick.current = false; return }
          const id = pick(e)
          if (id !== null) onSelectCluster(id)
        }}
      />
      {labelAnchors.map(a => (
        <button
          key={a.id}
          type="button"
          onClick={() => onSelectCluster(a.id)}
          className={`pointer-events-auto absolute -translate-x-1/2 -translate-y-1/2 rounded-md border px-2 py-0.5 text-[11px] font-semibold backdrop-blur-sm transition ${
            a.id === selectedClusterId
              ? backdrop === 'dark'
                ? 'border-indigo-400/70 bg-indigo-500/25 text-indigo-100'
                : 'border-indigo-400 bg-indigo-50 text-indigo-700'
              : backdrop === 'dark'
                ? 'border-white/15 bg-black/55 text-slate-200 hover:border-indigo-300/50'
                : 'border-gray-300 bg-white/85 text-gray-700 hover:border-indigo-400'
          }`}
          style={{ left: a.sx, top: a.sy }}
        >
          {a.label}
        </button>
      ))}
      {hover && (
        <div
          className="pointer-events-none absolute z-10 rounded bg-slate-900/85 px-2 py-1 text-[11px] text-white"
          style={{ left: hover.x + 12, top: hover.y + 12 }}
        >
          {hover.label}
        </div>
      )}
    </div>
  )
}
